import { Encounter, Observation, Practitioner, Reference } from "@medplum/fhirtypes";

import { formatISO } from "date-fns";

import { Systems } from "./enums/systems";
import { CommonObservationProps } from "./observation";

export const WeightUnitToCode = {
  g: "g",
  kg: "kg",
  lbs: "[lb_av]",
} as const;
export const WeightUnitChoices = {
  g: "g",
  kg: "kg",
  lbs: "lbs",
} as const;

export function getWeightObservation({
  status,
  subject,
  performer,
  encounter,
  effectiveDateTime,
  value,
  valueUnit,
}: CommonObservationProps & {
  encounter?: Reference<Encounter>;
  performer: Reference<Practitioner>;
  value: number;
  valueUnit: keyof typeof WeightUnitToCode;
}): Observation {
  return {
    resourceType: "Observation",
    meta: {
      profile: ["http://hl7.org/fhir/us/core/StructureDefinition/us-core-body-weight"],
    },
    encounter,
    subject,
    performer: [performer],
    status,
    effectiveDateTime: formatISO(effectiveDateTime),
    code: {
      coding: [
        {
          system: Systems.LOINC,
          code: "29463-7",
          display: "Weight",
        },
      ],
      text: "Weight",
    },
    valueQuantity: {
      value,
      system: Systems.UOM,
      unit: valueUnit,
      code: WeightUnitToCode[valueUnit],
    },
  };
}

export function getBodyWeightObservation({
  status,
  subject,
  encounter,
  effectiveDateTime,
  value,
  valueUnit,
}: CommonObservationProps & {
  encounter?: Reference<Encounter>;
  value: number;
  valueUnit: keyof typeof WeightUnitToCode;
}): Observation {
  return {
    resourceType: "Observation",
    encounter,
    subject,
    status,
    effectiveDateTime: formatISO(effectiveDateTime),
    code: {
      coding: [
        {
          system: Systems.LOINC,
          code: "3141-9",
          display: "Body weight Measured",
        },
      ],
      text: "Body weight Measured",
    },
    valueQuantity: {
      value,
      system: Systems.UOM,
      unit: valueUnit,
      code: WeightUnitToCode[valueUnit],
    },
  };
}

export type WeightUnit = keyof typeof WeightUnitChoices;

export function isWeightUnit(unit: string | undefined): unit is WeightUnit {
  return unit !== undefined && unit in GRAMS_PER_UNIT;
}

const GRAMS_PER_UNIT: Record<WeightUnit, number> = {
  [WeightUnitChoices.g]: 1,
  [WeightUnitChoices.kg]: 1000,
  [WeightUnitChoices.lbs]: 453.59237,
};

export function convertWeight(
  weight: number,
  fromWeightUnit: WeightUnit,
  toWeightUnit: WeightUnit,
): number {
  const fromGrams = GRAMS_PER_UNIT[fromWeightUnit];
  const weightInGrams = weight * fromGrams;

  const toGrams = GRAMS_PER_UNIT[toWeightUnit];
  const convertedWeight = weightInGrams / toGrams;
  return convertedWeight;
}

export function getWeightInGrams(observation: Observation): number | undefined {
  const value = observation.valueQuantity?.value;
  const unit = observation.valueQuantity?.unit;

  if (!value || !unit) {
    return undefined;
  }

  if (!isWeightUnit(unit)) {
    return undefined;
  }
  return convertWeight(value, unit, WeightUnitChoices.g);
}
